import { createClient } from '@supabase/supabase-js';
import { debounce } from './utils';
import type { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Storage key for auth - Keeping original key to prevent breaking auth sessions
const STORAGE_KEY = 'supabase.auth.token';

// Connection state management
let isConnected = false;
let connectionCheckInterval: number | null = null;

export interface UserProfile {
  id: string;
  display_name?: string;
  email: string;
}

class SupabaseService {
  private static instance: SupabaseService;
  private client: ReturnType<typeof createClient<Database>>;
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private authListener: (() => void) | null = null;
  private userDetailsCache: Record<string, UserProfile> = {};

  private constructor() {
    // Initialize client with retry logic and better error handling
    this.client = createClient<Database>(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storageKey: STORAGE_KEY,
        storage: window.localStorage,
      },
      global: {
        headers: {
          'x-client-info': 'zentime@1.0.0',
        },
        fetch: (url, options = {}) => {
          const MAX_RETRIES = 3;
          const INITIAL_RETRY_DELAY = 1000;

          const fetchWithRetry = async (
            retryCount = 0,
            delay = INITIAL_RETRY_DELAY
          ): Promise<Response> => {
            try {
              const response = await fetch(url, options);

              if (!response.ok && retryCount < MAX_RETRIES) {
                // Only retry on network errors or 5xx server errors
                if (!response.ok && (response.status >= 500 || response.status === 0)) {
                  console.log(`Retrying request (${retryCount + 1}/${MAX_RETRIES})...`);
                  await new Promise(resolve => setTimeout(resolve, delay));
                  return fetchWithRetry(retryCount + 1, delay * 2);
                }
              }

              return response;
            } catch (error) {
              if (retryCount < MAX_RETRIES) {
                console.log(`Retrying after error (${retryCount + 1}/${MAX_RETRIES}):`, error);
                await new Promise(resolve => setTimeout(resolve, delay));
                return fetchWithRetry(retryCount + 1, delay * 2);
              }
              throw error;
            }
          };

          return fetchWithRetry();
        },
      },
      db: {
        schema: 'public',
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });

    this.reinitialize = debounce(this.reinitialize.bind(this), 1000);

    // Debug: Log initial session
    this.client.auth
      .getSession()
      .then(({ data: { session } }) => {
        console.log('Initial session:', session ? 'Found' : 'None');
      })
      .catch(err => {
        console.error('Error getting initial session:', err);
      });

    // Start connection monitoring
    this.startConnectionMonitoring();

    // Clean up on window unload
    window.addEventListener('unload', () => {
      this.stopConnectionMonitoring();
    });
  }

  public static getInstance(): SupabaseService {
    if (!SupabaseService.instance) {
      SupabaseService.instance = new SupabaseService();
    }
    return SupabaseService.instance;
  }

  private async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise<void>(resolve => {
      try {
        // Remove previous auth listener if it exists
        if (this.authListener && typeof this.authListener === 'function') {
          this.authListener();
        }

        // Store the unsubscribe function returned by onAuthStateChange
        const {
          data: { subscription },
        } = this.client.auth.onAuthStateChange(event => {
          setTimeout(() => {
            if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
              this.reinitialize();
            }
          }, 0);
        });

        // Set the unsubscribe function as the authListener
        this.authListener = subscription.unsubscribe;

        this.initialized = true;
        resolve();
      } catch (error) {
        console.error('Failed to initialize Supabase client:', error);
        this.initialized = false;
        resolve();
      }
    });

    return this.initPromise;
  }

  private async reinitialize(): Promise<void> {
    this.initialized = false;
    this.initPromise = null;
    if (this.authListener && typeof this.authListener === 'function') {
      this.authListener();
      this.authListener = null;
    }
    await this.initialize();
  }

  public async getClient(): Promise<ReturnType<typeof createClient<Database>>> {
    await this.initialize();
    return this.client;
  }

  public async fetchUserDetails(userIds: string[]): Promise<UserProfile[]> {
    console.log('🔍 Fetching User Details - Start', { userIds });

    // Process each user ID
    const userDetailPromises = userIds.map(async userId => {
      // Check cache first
      if (this.userDetailsCache[userId]) {
        console.log('📦 Using Cached User Detail', { userId });
        return this.userDetailsCache[userId];
      }

      try {
        // Explicitly construct the full function URL
        const functionUrl = `${supabaseUrl}/functions/v1/get-user-details`;

        console.log('🌐 Function URL', { functionUrl });

        const session = await this.client.auth.getSession();
        console.log('🔐 Session Details', {
          hasSession: !!session.data.session,
          accessTokenPresent: !!session.data.session?.access_token,
        });

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.data.session?.access_token}`,
          },
          body: JSON.stringify({ userId }),
        });

        console.log('📡 Fetch Response', {
          status: response.status,
          ok: response.ok,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Fetch Error', {
            status: response.status,
            statusText: response.statusText,
            errorText,
          });
          throw new Error(`HTTP error! status: ${response.status}, text: ${errorText}`);
        }

        const profile = await response.json();
        console.log('✅ Fetched User Detail', { profile });

        // Cache the fetched user details
        this.userDetailsCache[userId] = profile;

        return profile;
      } catch (error) {
        console.error(`❌ Error fetching user details for ${userId}:`, error);
        return {
          id: userId,
          email: 'unknown@example.com',
        };
      }
    });

    // Wait for all user details to be fetched
    return Promise.all(userDetailPromises);
  }

  public getUserDisplayName(profile: UserProfile): string {
    console.log('🏷️ Get User Display Name', { profile });

    // Only use display_name if it exists and is not empty
    if (profile.display_name && profile.display_name.trim() !== '') {
      console.log('👤 Using Display Name', { displayName: profile.display_name });
      return profile.display_name;
    }

    // For shared views and projects, we still need to show something
    // But we should not default to email as a display name for the current user
    // This fallback is only used for showing collaborators
    if (profile.email) {
      console.log('📧 Using Email as Fallback', { email: profile.email });
      // Just use first part of email for privacy
      return profile.email.split('@')[0];
    }

    console.log('🆔 Using User ID as Last Resort', { id: profile.id });
    return profile.id;
  }

  // Check connection status with retry
  public async checkSupabaseConnection(): Promise<boolean> {
    const MAX_RETRIES = 3;
    const INITIAL_RETRY_DELAY = 1000;

    const checkWithRetry = async (
      retryCount = 0,
      delay = INITIAL_RETRY_DELAY
    ): Promise<boolean> => {
      try {
        // Check connection using a lightweight auth check instead of querying tables
        const { error } = await this.client.auth.getSession();

        // Connection is successful if we don't get an error
        const newConnectionState = !error;

        if (!newConnectionState && retryCount < MAX_RETRIES) {
          console.log(`Retrying connection check (${retryCount + 1}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return checkWithRetry(retryCount + 1, delay * 2);
        }

        if (newConnectionState !== isConnected) {
          isConnected = newConnectionState;
          console.log('Supabase connection status:', isConnected ? 'Connected' : 'Disconnected');
          if (!isConnected) {
            console.error('Connection error:', error);
          }
        }

        return isConnected;
      } catch (error) {
        if (retryCount < MAX_RETRIES) {
          console.log(`Retrying after error (${retryCount + 1}/${MAX_RETRIES}):`, error);
          await new Promise(resolve => setTimeout(resolve, delay));
          return checkWithRetry(retryCount + 1, delay * 2);
        }
        console.error('Supabase connection check error:', error);
        isConnected = false;
        return false;
      }
    };

    return checkWithRetry();
  }

  // Start periodic connection checks
  public startConnectionMonitoring(): void {
    if (connectionCheckInterval) return;

    // Check immediately
    this.checkSupabaseConnection();

    // Then check every 30 seconds
    connectionCheckInterval = window.setInterval(() => this.checkSupabaseConnection(), 30000);
  }

  // Stop connection monitoring
  public stopConnectionMonitoring(): void {
    if (connectionCheckInterval) {
      clearInterval(connectionCheckInterval);
      connectionCheckInterval = null;
    }
  }

  public getConnectionStatus(): boolean {
    return isConnected;
  }
}

// Export the singleton service
export const supabaseService = SupabaseService.getInstance();

// Export a direct client for simpler use cases (for backward compatibility)
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey: STORAGE_KEY,
    storage: window.localStorage,
    autoRefreshToken: true,
  },
});

// For backward compatibility
export async function getSupabase(): Promise<ReturnType<typeof createClient<Database>>> {
  return await supabaseService.getClient();
}

// Export connection management functions for backward compatibility
export const checkSupabaseConnection = () => supabaseService.checkSupabaseConnection();
export const startConnectionMonitoring = () => supabaseService.startConnectionMonitoring();
export const stopConnectionMonitoring = () => supabaseService.stopConnectionMonitoring();
export const getConnectionStatus = () => supabaseService.getConnectionStatus();
